import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Button } from "../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { ScrollArea } from "../components/ui/scroll-area";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import BackButton from "../components/BackButton";
import LanguageSwitcher from "../components/LanguageSwitcher";
import {
  Beaker, FlaskConical, MessageSquare, FileText, Database, LogOut,
  Brain, Shield, Users, BarChart3, ThumbsUp, ThumbsDown, Loader2,
  Eye, TrendingUp, Search, Mail, Ban, CheckCircle, Trash2,
  Upload, Archive, Package, Sparkles, Factory, Copy, FileUp, Table2
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [allowed, setAllowed] = useState(null);
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [userSearch, setUserSearch] = useState("");
  const [chats, setChats] = useState([]);
  const [forms, setForms] = useState([]);
  const [feedback, setFeedback] = useState([]);
  const [topics, setTopics] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bulkJobs, setBulkJobs] = useState([]);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkUploadPct, setBulkUploadPct] = useState(0);
  const [bulkUploadName, setBulkUploadName] = useState("");
  const [bulkActiveJobId, setBulkActiveJobId] = useState(null);
  const [bulkShared, setBulkShared] = useState(true);
  const [dragOver, setDragOver] = useState(false);
  const [suppliers, setSuppliers] = useState([]);
  const [dups, setDups] = useState({ groups: [], total_duplicates: 0 });
  const [supUploading, setSupUploading] = useState(false);
  const [dupCleaning, setDupCleaning] = useState(false);
  const [dbCollections, setDbCollections] = useState([]);
  const [dbActive, setDbActive] = useState(null);
  const [dbData, setDbData] = useState(null);
  const [dbLoading, setDbLoading] = useState(false);
  const [dbSearch, setDbSearch] = useState("");

  useEffect(() => {
    axios.get(`${API}/admin/check`).then((r) => {
      if (!r.data.is_admin) { setAllowed(false); return; }
      setAllowed(true);
      loadAll();
    }).catch(() => setAllowed(false));
  }, []);

  const loadAll = async () => {
    setLoading(true);
    // Load every endpoint independently so a single failure can't wipe the whole dashboard
    const safeGet = async (url, setter, fallback) => {
      try {
        const r = await axios.get(`${API}${url}`);
        setter(r.data);
      } catch (e) {
        console.warn(`[admin] failed to load ${url}:`, e?.response?.status, e?.message);
        if (fallback !== undefined) setter(fallback);
      }
    };
    await Promise.all([
      safeGet("/admin/stats", setStats),
      safeGet("/admin/users", setUsers, []),
      safeGet("/admin/chats?limit=100", setChats, []),
      safeGet("/admin/formulations?limit=100", setForms, []),
      safeGet("/admin/feedback?limit=100", setFeedback, []),
      safeGet("/admin/top-topics", setTopics, []),
      safeGet("/admin/bulk-import", setBulkJobs, []),
      safeGet("/admin/suppliers", setSuppliers, []),
      safeGet("/admin/duplicates", setDups, { groups: [], total_duplicates: 0 }),
      safeGet("/admin/db/collections", (d) => setDbCollections(d.collections || []), { collections: [] }),
    ]);
    setLoading(false);
  };

  const browseCollection = async (name, page = 0, q = "") => {
    setDbActive(name);
    setDbLoading(true);
    try {
      const r = await axios.get(`${API}/admin/db/${name}?page=${page}&limit=20&q=${encodeURIComponent(q)}`);
      setDbData(r.data);
    } catch (e) { alert("Failed to load collection"); }
    finally { setDbLoading(false); }
  };

  const promoteSupplier = async (s) => {
    try {
      await axios.post(`${API}/admin/suppliers/${s.id}/promote`);
      setSuppliers((prev) => prev.map((x) => (x.id === s.id ? { ...x, promoted: true } : x)));
    } catch (e) { alert("Promote failed"); }
  };

  const deleteSupplier = async (s) => {
    if (!window.confirm(`Delete supplier entry "${s.supplier} — ${s.product}"?`)) return;
    try {
      await axios.delete(`${API}/admin/suppliers/${s.id}`);
      setSuppliers((prev) => prev.filter((x) => x.id !== s.id));
    } catch (e) { alert("Delete failed"); }
  };

  const deleteChat = async (c) => {
    if (!window.confirm(`Delete chat "${c.title}"? This permanently removes it from the database.`)) return;
    try {
      await axios.delete(`${API}/admin/chats/${c.session_id}`);
      setChats((prev) => prev.filter((x) => x.session_id !== c.session_id));
    } catch (e) { alert("Delete failed"); }
  };

  const deleteAllChats = async () => {
    if (!window.confirm(`Delete ALL ${chats.length} chat sessions (all users)? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/chats`);
      setChats([]);
    } catch (e) { alert("Delete failed"); }
  };

  const deleteFormulation = async (f) => {
    if (!window.confirm(`Delete this formulation? This permanently removes it from the database.`)) return;
    try {
      await axios.delete(`${API}/admin/formulations/${f.id}`);
      setForms((prev) => prev.filter((x) => x.id !== f.id));
    } catch (e) { alert("Delete failed"); }
  };

  const deleteAllFormulations = async () => {
    if (!window.confirm(`Delete ALL ${forms.length} formulations (all users)? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/formulations`);
      setForms([]);
    } catch (e) { alert("Delete failed"); }
  };

  const uploadSupplierFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setSupUploading(true);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      const r = await axios.post(`${API}/admin/suppliers/upload`, fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 180000 });
      if (r.data.count > 0) {
        const list = await axios.get(`${API}/admin/suppliers`);
        setSuppliers(list.data);
      } else {
        alert("No new supplier data added (empty file or duplicate of an existing entry)");
      }
    } catch (err) {
      alert(err?.response?.data?.detail || "Upload failed");
    } finally {
      setSupUploading(false);
      e.target.value = "";
    }
  };

  const deleteDupEntries = async (ids) => {
    try {
      await axios.post(`${API}/admin/duplicates/delete`, { ids });
      const r = await axios.get(`${API}/admin/duplicates`);
      setDups(r.data);
    } catch (e) { alert("Delete failed"); }
  };

  const autoCleanDups = async () => {
    if (!window.confirm(`Auto-clean will delete ${dups.total_duplicates} duplicate entries (keeping the oldest copy of each). Continue?`)) return;
    setDupCleaning(true);
    try {
      const r = await axios.post(`${API}/admin/duplicates/auto-clean`);
      alert(`Deleted ${r.data.deleted} duplicate entries`);
      const d = await axios.get(`${API}/admin/duplicates`);
      setDups(d.data);
    } catch (e) { alert("Auto-clean failed"); }
    finally { setDupCleaning(false); }
  };

  // Poll active bulk import job
  useEffect(() => {
    if (!bulkActiveJobId) return;
    const t = setInterval(async () => {
      try {
        const r = await axios.get(`${API}/admin/bulk-import/${bulkActiveJobId}`);
        setBulkJobs((prev) => {
          const others = prev.filter((j) => j.id !== r.data.id);
          return [r.data, ...others];
        });
        if (r.data.status === "completed" || r.data.status === "failed") {
          setBulkActiveJobId(null);
          loadAll();
        }
      } catch (e) {}
    }, 2000);
    return () => clearInterval(t);
  }, [bulkActiveJobId]);

  const handleBulkUpload = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".zip")) {
      alert("Please upload a .zip file containing PDFs/TXTs");
      return;
    }
    const MAX_MB = 95;
    const sizeMB = file.size / 1024 / 1024;
    if (sizeMB > MAX_MB) {
      alert(`This ZIP is ${sizeMB.toFixed(1)} MB — too large for direct upload.\n\nPlease split it into smaller ZIPs (each under ${MAX_MB} MB) and upload them one at a time. The chunks will be merged in the knowledge base automatically.`);
      return;
    }
    setBulkUploading(true);
    setBulkUploadName(file.name);
    setBulkUploadPct(0);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("shared", String(bulkShared));
      const res = await axios.post(`${API}/admin/bulk-import`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (evt) => {
          if (evt.total) setBulkUploadPct(Math.round((evt.loaded * 100) / evt.total));
        },
      });
      setBulkActiveJobId(res.data.job_id);
      loadAll();
    } catch (err) {
      const status = err.response?.status;
      let msg = err.response?.data?.detail || err.message;
      if (status === 413) {
        msg = `Upload rejected: ZIP too large (limit ~95 MB on production). Please split your ZIP into smaller parts and upload them separately.`;
      } else if (status === 524 || status === 502 || status === 503) {
        msg = `Server is busy (${status}). Please wait 30 seconds and try again. If your ZIP is huge, split it into smaller parts.`;
      }
      alert("Upload failed: " + msg);
    } finally {
      setBulkUploading(false);
      setBulkUploadPct(0);
      setBulkUploadName("");
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleBulkUpload(file);
  };

  const deleteBulkJob = async (jobId) => {
    if (!window.confirm(`Delete this import job AND all the knowledge chunks it created? This cannot be undone.`)) return;
    try {
      const r = await axios.delete(`${API}/admin/bulk-import/${jobId}`);
      alert(`Deleted ${r.data.chunks_deleted} chunks.`);
      setBulkJobs((prev) => prev.filter((j) => j.id !== jobId));
      loadAll();
    } catch (e) { alert(e.response?.data?.detail || "Delete failed"); }
  };

  const cancelBulkJob = async (jobId) => {
    if (!window.confirm("Stop this in-progress import? Chunks already inserted will remain.")) return;
    try {
      await axios.post(`${API}/admin/bulk-import/${jobId}/cancel`);
      // Mark locally so user sees immediate feedback
      setBulkJobs((prev) => prev.map((j) => j.id === jobId ? { ...j, cancel_requested: true } : j));
    } catch (e) { alert(e.response?.data?.detail || "Cancel failed"); }
  };

  const [chunksDialog, setChunksDialog] = useState(null); // { jobId, items, total, page }
  const viewBulkChunks = async (jobId, page = 0) => {
    try {
      const r = await axios.get(`${API}/admin/bulk-import/${jobId}/chunks?page=${page}&limit=30`);
      setChunksDialog({ jobId, ...r.data });
    } catch (e) { alert("Failed to load chunks"); }
  };

  const openChat = async (sid) => {
    try { const r = await axios.get(`${API}/admin/chats/${sid}`); setSelectedChat(r.data); } catch (e) {}
  };

  const blockUser = async (u) => {
    if (!window.confirm(`Block ${u.email}? They won't be able to log in until you unblock them.`)) return;
    try {
      await axios.post(`${API}/admin/users/${u.id}/block`);
      setUsers((prev) => prev.map((x) => x.id === u.id ? { ...x, is_blocked: true } : x));
    } catch (e) { alert(e.response?.data?.detail || "Failed to block user"); }
  };

  const unblockUser = async (u) => {
    try {
      await axios.post(`${API}/admin/users/${u.id}/unblock`);
      setUsers((prev) => prev.map((x) => x.id === u.id ? { ...x, is_blocked: false } : x));
    } catch (e) { alert(e.response?.data?.detail || "Failed to unblock user"); }
  };

  const deleteUser = async (u) => {
    if (!window.confirm(`PERMANENTLY DELETE ${u.email} and ALL their data (chats, formulations, documents, training)?\n\nThis CANNOT be undone. Shared knowledge entries will remain.`)) return;
    if (!window.confirm(`Are you absolutely sure? Type-confirm not required, but this is your last chance.`)) return;
    try {
      await axios.delete(`${API}/admin/users/${u.id}`);
      setUsers((prev) => prev.filter((x) => x.id !== u.id));
      loadAll();
    } catch (e) { alert(e.response?.data?.detail || "Failed to delete user"); }
  };

  const promoteUserData = async (u) => {
    if (!window.confirm(`Promote ${u.email}'s data to shared Knowledge Base?`)) return;
    try {
      const r = await axios.post(`${API}/admin/users/${u.id}/promote-data`);
      const p = r.data.promoted || {};
      alert(`Promoted ${r.data.total} entries (Chats: ${p.chats}, Formulations: ${p.formulations}, Training: ${p.training})`);
      loadAll();
    } catch (e) {
      alert(e.response?.data?.detail || "Promotion failed");
    }
  };

  const navItems = [
    { icon: MessageSquare, label: "Chat", path: "/chat" },
    { icon: FlaskConical, label: "Formulation", path: "/formulation" },
    { icon: FileText, label: "Documents", path: "/documents" },
    { icon: Database, label: "Knowledge", path: "/knowledge" },
    { icon: Brain, label: "Training", path: "/training" },
    { icon: Shield, label: "Admin", path: "/admin" },
  ];

  if (allowed === null) {
    return <div className="min-h-screen flex items-center justify-center bg-slate-50"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>;
  }

  // Filter users by name or email (case-insensitive)
  const q = userSearch.trim().toLowerCase();
  const filteredUsers = q
    ? users.filter((u) =>
        (u.name || "").toLowerCase().includes(q) ||
        (u.email || "").toLowerCase().includes(q)
      )
    : users;
  if (allowed === false) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 p-6">
        <Shield className="w-16 h-16 text-red-400 mb-4" />
        <h1 className="font-heading text-2xl font-bold text-slate-900 mb-2">Access Denied</h1>
        <p className="text-slate-500 mb-4">You don't have admin permissions.</p>
        <Button onClick={() => navigate("/dashboard")}>Back to Dashboard</Button>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-slate-50">
      <div className="w-72 bg-white border-r border-slate-200 flex flex-col">
        <div className="p-4 border-b border-slate-200">
          <Link to="/dashboard" className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-full bg-orange-500 flex items-center justify-center"><Beaker className="w-5 h-5 text-white" /></div>
            <span className="font-heading font-bold text-lg text-slate-900">ElastoAI</span>
          </Link>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {navItems.map((item) => (
            <Link key={item.path} to={item.path}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg ${item.path === "/admin" ? "bg-slate-100 text-slate-900" : "text-slate-600 hover:bg-slate-50"}`}>
              <item.icon className="w-5 h-5" /><span className="font-medium">{item.label}</span>
              {item.label === "Admin" && <span className="ml-auto text-xs px-2 py-0.5 bg-red-100 text-red-600 rounded-full">Owner</span>}
            </Link>
          ))}
        </nav>
        <div className="p-4 border-t border-slate-200 flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-orange-500 flex items-center justify-center text-white text-sm font-medium">{user?.name?.charAt(0).toUpperCase()}</div>
          <div className="flex-1 min-w-0"><p className="text-sm font-medium text-slate-900 truncate">{user?.name}</p><p className="text-xs text-red-600">Admin</p></div>
          <button onClick={logout} className="p-2 hover:bg-slate-100 rounded-lg"><LogOut className="w-4 h-4 text-slate-500" /></button>
        </div>
      </div>

      <div className="flex-1 overflow-hidden flex flex-col">
        <header className="h-16 bg-white border-b border-slate-200 flex items-center px-6 gap-3">
          <BackButton />
          <Shield className="w-5 h-5 text-red-500" />
          <h1 className="font-heading font-semibold text-slate-900">Admin Dashboard</h1>
          <span className="ml-2 text-xs px-2 py-0.5 bg-red-100 text-red-600 rounded-full font-medium">Owner-only</span>
          <div className="flex-1" />
          <LanguageSwitcher className="mr-2" />
          <Button variant="outline" size="sm" onClick={loadAll} disabled={loading} data-testid="admin-refresh">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Refresh"}
          </Button>
        </header>

        <div className="flex-1 overflow-y-auto p-6">
          <div className="max-w-7xl mx-auto">
            {stats && (
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-6">
                {[
                  ["Users", stats.users.total, `${stats.users.active_7d} active 7d`],
                  ["Chat Sessions", stats.chats.sessions, `${stats.chats.messages} messages`],
                  ["Formulations", stats.formulations, ""],
                  ["Documents", stats.documents, ""],
                  ["Knowledge", stats.knowledge.total, `${stats.knowledge.shared} shared`],
                  ["Feedback", `${stats.feedback.up}↑ ${stats.feedback.down}↓`, ""],
                ].map(([lbl, v, sub]) => (
                  <div key={lbl} className="bg-white rounded-xl border border-slate-200 p-4">
                    <p className="text-xs text-slate-500 uppercase tracking-wider">{lbl}</p>
                    <p className="font-heading text-2xl font-bold text-slate-900">{v}</p>
                    {sub && <p className="text-xs text-slate-400 mt-1">{sub}</p>}
                  </div>
                ))}
              </div>
            )}

            <Tabs value={tab} onValueChange={setTab}>
              <TabsList className="bg-white border border-slate-200">
                <TabsTrigger value="overview" data-testid="admin-tab-overview"><BarChart3 className="w-4 h-4 mr-1" /> Overview</TabsTrigger>
                <TabsTrigger value="users" data-testid="admin-tab-users"><Users className="w-4 h-4 mr-1" /> Users</TabsTrigger>
                <TabsTrigger value="chats" data-testid="admin-tab-chats"><MessageSquare className="w-4 h-4 mr-1" /> Chats</TabsTrigger>
                <TabsTrigger value="formulations" data-testid="admin-tab-forms"><FlaskConical className="w-4 h-4 mr-1" /> Formulations</TabsTrigger>
                <TabsTrigger value="feedback" data-testid="admin-tab-feedback"><ThumbsUp className="w-4 h-4 mr-1" /> Feedback</TabsTrigger>
                <TabsTrigger value="topics" data-testid="admin-tab-topics"><TrendingUp className="w-4 h-4 mr-1" /> Topics</TabsTrigger>
                <TabsTrigger value="bulk-import" data-testid="admin-tab-bulk-import"><Archive className="w-4 h-4 mr-1" /> Bulk Import</TabsTrigger>
                <TabsTrigger value="duplicates" data-testid="admin-tab-duplicates"><Copy className="w-4 h-4 mr-1" /> Duplicates ({dups.total_duplicates})</TabsTrigger>
                <TabsTrigger value="database" data-testid="admin-tab-database"><Table2 className="w-4 h-4 mr-1" /> Database</TabsTrigger>
              </TabsList>

              <TabsContent value="database" className="mt-4">
                <div className="bg-white rounded-xl border border-slate-200 p-5">
                  <div className="mb-4">
                    <h3 className="font-heading font-semibold text-slate-900 flex items-center gap-2"><Database className="w-4 h-4 text-orange-500" /> Database Explorer (read-only)</h3>
                    <p className="text-xs text-slate-500 mt-1">Live view of the MongoDB collections. This is a read-only window — passwords are redacted, and nothing here can modify data. Every count and record is queried directly from the database.</p>
                  </div>

                  <div className="flex flex-wrap gap-2 mb-4">
                    {dbCollections.map((c) => (
                      <button key={c.name} onClick={() => { setDbSearch(""); browseCollection(c.name, 0, ""); }}
                        data-testid={`db-collection-${c.name}`}
                        className={`px-3 py-1.5 rounded-lg border text-sm font-medium flex items-center gap-2 ${dbActive === c.name ? "bg-orange-500 text-white border-orange-500" : "bg-white text-slate-700 border-slate-200 hover:border-orange-300"}`}>
                        {c.name}
                        <span className={`text-xs px-1.5 py-0.5 rounded-full ${dbActive === c.name ? "bg-white/20" : "bg-slate-100 text-slate-500"}`}>{c.count}</span>
                      </button>
                    ))}
                  </div>

                  {!dbActive ? (
                    <p className="text-slate-400 text-sm py-8 text-center">Select a collection above to browse its records.</p>
                  ) : (
                    <>
                      <div className="flex items-center gap-2 mb-3">
                        <div className="relative flex-1 max-w-sm">
                          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                          <input value={dbSearch} onChange={(e) => setDbSearch(e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter") browseCollection(dbActive, 0, dbSearch); }}
                            placeholder={`Search in ${dbActive}... (Enter)`}
                            data-testid="db-search"
                            className="w-full pl-9 pr-3 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-300" />
                        </div>
                        <span className="text-xs text-slate-500">{dbData?.total ?? 0} records</span>
                      </div>

                      {dbLoading ? (
                        <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
                      ) : (dbData?.documents || []).length === 0 ? (
                        <p className="text-slate-400 text-sm py-8 text-center">No records.</p>
                      ) : (
                        <div className="space-y-2">
                          {dbData.documents.map((doc, i) => (
                            <details key={i} className="border border-slate-200 rounded-lg overflow-hidden" data-testid={`db-record-${i}`}>
                              <summary className="cursor-pointer px-3 py-2 bg-slate-50 text-sm text-slate-700 hover:bg-slate-100 font-mono truncate">
                                {doc.title || doc.filename || doc.email || doc.supplier || doc.session_id || doc.id || `record ${i + 1}`}
                              </summary>
                              <pre className="p-3 text-xs text-slate-700 overflow-x-auto whitespace-pre-wrap bg-white border-t border-slate-100">{JSON.stringify(doc, null, 2)}</pre>
                            </details>
                          ))}
                        </div>
                      )}

                      {dbData && dbData.total > dbData.limit && (
                        <div className="flex items-center justify-between text-sm pt-3 mt-3 border-t border-slate-200">
                          <Button size="sm" variant="outline" disabled={dbData.page === 0} onClick={() => browseCollection(dbActive, dbData.page - 1, dbSearch)} data-testid="db-prev">← Prev</Button>
                          <span className="text-slate-500">Page {dbData.page + 1} of {Math.ceil(dbData.total / dbData.limit)}</span>
                          <Button size="sm" variant="outline" disabled={(dbData.page + 1) * dbData.limit >= dbData.total} onClick={() => browseCollection(dbActive, dbData.page + 1, dbSearch)} data-testid="db-next">Next →</Button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </TabsContent>

              <TabsContent value="suppliers" className="mt-4">
                <div className="bg-white rounded-xl border border-slate-200 p-5">
                  <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                    <div>
                      <h3 className="font-heading font-semibold text-slate-900 flex items-center gap-2"><Factory className="w-4 h-4 text-orange-500" /> Supplier Library</h3>
                      <p className="text-xs text-slate-500 mt-1">TDS/SDS data auto-captured whenever any user uploads a datasheet from a new supplier. Promote an entry to add it to the shared knowledge base for all users.</p>
                    </div>
                    <label className={`inline-flex items-center gap-2 px-4 py-2 rounded-full cursor-pointer text-sm font-medium ${supUploading ? "bg-blue-50 text-blue-700" : "bg-slate-900 hover:bg-slate-800 text-white"}`}>
                      {supUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileUp className="w-4 h-4" />}
                      {supUploading ? "Uploading & detecting supplier..." : "Upload supplier TDS/SDS"}
                      <input type="file" multiple accept=".pdf,.xlsx,.xls,.txt,.csv" className="hidden" onChange={uploadSupplierFiles} disabled={supUploading} data-testid="admin-supplier-upload" />
                    </label>
                  </div>
                  {suppliers.length === 0 ? (
                    <p className="text-slate-400 text-sm py-6 text-center">No supplier data captured yet — it appears automatically when users upload TDS/SDS files, or upload manually above.</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-slate-50 border-b border-slate-200">
                          <tr>
                            <th className="text-left px-3 py-2 font-medium text-slate-600">Supplier</th>
                            <th className="text-left px-3 py-2 font-medium text-slate-600">Product</th>
                            <th className="text-left px-3 py-2 font-medium text-slate-600">Category</th>
                            <th className="text-left px-3 py-2 font-medium text-slate-600">Type</th>
                            <th className="text-left px-3 py-2 font-medium text-slate-600">File</th>
                            <th className="text-center px-3 py-2 font-medium text-slate-600">Status</th>
                            <th className="text-right px-3 py-2 font-medium text-slate-600">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {suppliers.map((s) => (
                            <tr key={s.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`admin-supplier-${s.id}`}>
                              <td className="px-3 py-2 text-slate-900 font-medium">{s.supplier}</td>
                              <td className="px-3 py-2 text-slate-600">{s.product || "—"}</td>
                              <td className="px-3 py-2 text-slate-500 text-xs capitalize">{(s.category || "").replace("_", " ")}</td>
                              <td className="px-3 py-2"><span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${s.kind === "sds" ? "bg-rose-50 text-rose-700" : "bg-amber-50 text-amber-700"}`}>{(s.kind || "tds").toUpperCase()}</span></td>
                              <td className="px-3 py-2 text-slate-500 text-xs max-w-[180px] truncate">{s.filename}</td>
                              <td className="px-3 py-2 text-center">
                                {s.promoted ? (
                                  <span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded-full font-medium">In shared KB</span>
                                ) : (
                                  <span className="text-xs px-2 py-0.5 bg-slate-100 text-slate-600 rounded-full font-medium">New</span>
                                )}
                              </td>
                              <td className="px-3 py-2 text-right">
                                <div className="flex items-center gap-1 justify-end">
                                  {!s.promoted && (
                                    <button onClick={() => promoteSupplier(s)} title="Promote to shared knowledge base" data-testid={`promote-supplier-${s.id}`}
                                      className="p-1.5 text-purple-600 hover:bg-purple-50 rounded-lg">
                                      <Sparkles className="w-4 h-4" />
                                    </button>
                                  )}
                                  <button onClick={() => deleteSupplier(s)} title="Delete supplier entry" data-testid={`delete-supplier-${s.id}`}
                                    className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg">
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </TabsContent>

              <TabsContent value="duplicates" className="mt-4">
                <div className="bg-white rounded-xl border border-slate-200 p-5">
                  <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                    <div>
                      <h3 className="font-heading font-semibold text-slate-900 flex items-center gap-2"><Copy className="w-4 h-4 text-orange-500" /> Duplicate Knowledge Entries</h3>
                      <p className="text-xs text-slate-500 mt-1">{dups.total_duplicates} duplicate {dups.total_duplicates === 1 ? "entry" : "entries"} found in {dups.groups.length} group{dups.groups.length === 1 ? "" : "s"} (same title + content). The oldest copy of each group is kept.</p>
                    </div>
                    {dups.total_duplicates > 0 && (
                      <Button onClick={autoCleanDups} disabled={dupCleaning} data-testid="admin-dup-autoclean"
                        className="bg-red-500 hover:bg-red-600 text-white rounded-full">
                        {dupCleaning ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />}
                        Auto-clean all duplicates
                      </Button>
                    )}
                  </div>
                  {dups.groups.length === 0 ? (
                    <p className="text-slate-400 text-sm py-6 text-center">No duplicates found — the knowledge base is clean.</p>
                  ) : (
                    <div className="space-y-4">
                      {dups.groups.map((g, gi) => (
                        <div key={gi} className="border border-slate-200 rounded-lg overflow-hidden" data-testid={`admin-dup-group-${gi}`}>
                          <div className="bg-slate-50 px-4 py-2 text-xs font-medium text-slate-600 flex items-center justify-between">
                            <span>{g.length} identical copies — "{g[0].title}"</span>
                            <button onClick={() => deleteDupEntries(g.slice(1).map((x) => x.id))} data-testid={`admin-dup-clean-group-${gi}`}
                              className="text-red-500 hover:text-red-600 font-medium">Delete {g.length - 1} cop{g.length - 1 === 1 ? "y" : "ies"}, keep oldest</button>
                          </div>
                          {g.map((entry, ei) => (
                            <div key={entry.id} className="px-4 py-2 border-t border-slate-100 flex items-center gap-3 text-sm">
                              <span className={`text-xs px-2 py-0.5 rounded-full font-medium flex-shrink-0 ${ei === 0 ? "bg-green-100 text-green-700" : "bg-red-50 text-red-600"}`}>
                                {ei === 0 ? "Keep (oldest)" : "Duplicate"}
                              </span>
                              <div className="flex-1 min-w-0">
                                <p className="text-slate-700 truncate">{entry.preview || entry.title}</p>
                                <p className="text-xs text-slate-400">{entry.category} • {entry.is_shared ? "shared" : "private"} • {entry.created_at?.slice(0, 19).replace("T", " ")}</p>
                              </div>
                              {ei > 0 && (
                                <button onClick={() => deleteDupEntries([entry.id])} title="Delete this copy" data-testid={`admin-dup-delete-${entry.id}`}
                                  className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg flex-shrink-0">
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                          ))}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </TabsContent>

              <TabsContent value="overview" className="mt-4">
                <div className="grid lg:grid-cols-2 gap-4">
                  <div className="bg-white rounded-xl border border-slate-200 p-5">
                    <h3 className="font-heading font-semibold text-slate-900 mb-3 flex items-center gap-2"><TrendingUp className="w-4 h-4 text-orange-500" />Hot Topics (last 100 chats)</h3>
                    {topics.length === 0 ? <p className="text-slate-400 text-sm">No data yet</p> : topics.slice(0, 12).map((t) => (
                      <div key={t.topic} className="flex items-center justify-between py-1.5 border-b border-slate-100 last:border-0">
                        <span className="text-sm text-slate-700 capitalize">{t.topic}</span>
                        <span className="text-xs px-2 py-0.5 bg-orange-50 text-orange-700 rounded-full">{t.count}×</span>
                      </div>
                    ))}
                  </div>
                  <div className="bg-white rounded-xl border border-slate-200 p-5">
                    <h3 className="font-heading font-semibold text-slate-900 mb-3 flex items-center gap-2"><Users className="w-4 h-4 text-orange-500" />Top Users</h3>
                    {users.slice(0, 8).map((u) => (
                      <div key={u.id} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-slate-900 truncate">{u.name} {u.is_admin && <span className="text-xs text-red-600 ml-1">(admin)</span>}</p>
                          <p className="text-xs text-slate-500 truncate">{u.email}</p>
                        </div>
                        <span className="text-xs text-slate-500 ml-2">{u.chats_count} chats</span>
                      </div>
                    ))}
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="users" className="mt-4">
                <div className="mb-3 relative">
                  <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                  <input
                    type="text"
                    value={userSearch}
                    onChange={(e) => setUserSearch(e.target.value)}
                    placeholder="Search users by name or email..."
                    data-testid="admin-user-search"
                    className="w-full pl-9 pr-3 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-300"
                  />
                </div>
                <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr>
                        <th className="text-left px-4 py-2 font-medium text-slate-600">Name</th>
                        <th className="text-left px-4 py-2 font-medium text-slate-600">Email</th>
                        <th className="text-center px-4 py-2 font-medium text-slate-600">Status</th>
                        <th className="text-right px-4 py-2 font-medium text-slate-600">Chats</th>
                        <th className="text-right px-4 py-2 font-medium text-slate-600">Formulations</th>
                        <th className="text-right px-4 py-2 font-medium text-slate-600">Docs</th>
                        <th className="text-left px-4 py-2 font-medium text-slate-600">Joined</th>
                        <th className="text-right px-4 py-2 font-medium text-slate-600">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredUsers.length === 0 && (
                        <tr><td colSpan="8" className="px-4 py-6 text-center text-sm text-slate-400">No users match "{userSearch}"</td></tr>
                      )}
                      {filteredUsers.map((u) => (
                        <tr key={u.id} className={`border-b border-slate-100 hover:bg-slate-50 ${u.is_blocked ? "bg-red-50/50" : ""}`} data-testid={`admin-user-${u.id}`}>
                          <td className="px-4 py-2 text-slate-900">
                            {u.name}
                            {u.is_admin && <span className="text-xs px-1.5 py-0.5 bg-red-100 text-red-600 rounded ml-1">admin</span>}
                          </td>
                          <td className="px-4 py-2 text-slate-600">{u.email}</td>
                          <td className="px-4 py-2 text-center">
                            {u.is_blocked ? (
                              <span className="text-xs px-2 py-0.5 bg-red-100 text-red-700 rounded-full font-medium">Blocked</span>
                            ) : (
                              <span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded-full font-medium">Active</span>
                            )}
                          </td>
                          <td className="px-4 py-2 text-right">{u.chats_count}</td>
                          <td className="px-4 py-2 text-right">{u.formulations_count}</td>
                          <td className="px-4 py-2 text-right">{u.documents_count}</td>
                          <td className="px-4 py-2 text-slate-500 text-xs">{new Date(u.created_at).toLocaleDateString()}</td>
                          <td className="px-4 py-2 text-right">
                            {u.is_admin ? (
                              <span className="text-xs text-slate-400">—</span>
                            ) : (
                              <div className="flex items-center gap-1 justify-end">
                                <button onClick={() => promoteUserData(u)} data-testid={`promote-user-${u.id}`}
                                  title="Promote data to shared KB"
                                  className="p-1.5 text-purple-600 hover:bg-purple-50 rounded-lg">
                                  <Sparkles className="w-4 h-4" />
                                </button>
                                {u.is_blocked ? (
                                  <button onClick={() => unblockUser(u)} data-testid={`unblock-user-${u.id}`} title="Unblock user"
                                    className="p-1.5 text-green-600 hover:bg-green-50 rounded-lg">
                                    <CheckCircle className="w-4 h-4" />
                                  </button>
                                ) : (
                                  <button onClick={() => blockUser(u)} data-testid={`block-user-${u.id}`} title="Block user"
                                    className="p-1.5 text-amber-600 hover:bg-amber-50 rounded-lg">
                                    <Ban className="w-4 h-4" />
                                  </button>
                                )}
                                <button onClick={() => deleteUser(u)} data-testid={`delete-user-${u.id}`} title="Delete user and all their data"
                                  className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg">
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="text-xs text-slate-500 mt-3">
                  <Ban className="w-3 h-3 inline mr-1" /> <b>Block</b>: prevents login, keeps data •
                  <Trash2 className="w-3 h-3 inline mx-1" /> <b>Delete</b>: permanent removal of user + all their data (chats, formulations, docs, training). Shared knowledge entries remain.
                </p>
              </TabsContent>

              <TabsContent value="chats" className="mt-4">
                <div className="flex justify-end mb-3">
                  {chats.length > 0 && (
                    <Button variant="outline" size="sm" onClick={deleteAllChats} data-testid="admin-delete-all-chats"
                      className="text-red-600 border-red-200 hover:bg-red-50">
                      <Trash2 className="w-4 h-4 mr-1" /> Delete All ({chats.length})
                    </Button>
                  )}
                </div>
                <div className="space-y-2">
                  {chats.map((c) => (
                    <div key={c.session_id} className="bg-white rounded-xl border border-slate-200 p-4 flex items-center gap-3" data-testid={`admin-chat-${c.session_id}`}>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-slate-900 truncate">{c.title}</p>
                        <div className="flex items-center gap-3 text-xs text-slate-500 mt-1">
                          <span><Mail className="w-3 h-3 inline mr-1" />{c.user_email}</span>
                          <span>{c.messages?.length || 0} msgs</span>
                          <span>{new Date(c.updated_at).toLocaleString()}</span>
                        </div>
                      </div>
                      <Button variant="outline" size="sm" onClick={() => openChat(c.session_id)} data-testid={`view-chat-${c.session_id}`}>
                        <Eye className="w-4 h-4 mr-1" /> View
                      </Button>
                      <button onClick={() => deleteChat(c)} title="Delete chat" data-testid={`delete-chat-${c.session_id}`}
                        className="p-2 text-red-500 hover:bg-red-50 rounded-lg">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>

                <Dialog open={!!selectedChat} onOpenChange={(o) => !o && setSelectedChat(null)}>
                  <DialogContent className="sm:max-w-3xl max-h-[85vh]">
                    <DialogHeader><DialogTitle>{selectedChat?.title}</DialogTitle></DialogHeader>
                    <div className="text-xs text-slate-500 -mt-2 mb-2">{selectedChat?.user_email} • {selectedChat?.user_name}</div>
                    <ScrollArea className="h-[60vh] pr-3">
                      {selectedChat?.messages?.map((m, i) => (
                        <div key={i} className={`mb-3 p-3 rounded-lg ${m.role === "user" ? "bg-slate-900 text-white" : "bg-slate-50 text-slate-900"}`}>
                          <div className="text-xs font-medium opacity-70 mb-1">{m.role.toUpperCase()}</div>
                          <div className="prose prose-sm max-w-none">
                            {m.role === "assistant" ? <ReactMarkdown remarkPlugins={[remarkGfm]}>{m.content}</ReactMarkdown> : <pre className="whitespace-pre-wrap font-sans">{m.content}</pre>}
                          </div>
                        </div>
                      ))}
                    </ScrollArea>
                  </DialogContent>
                </Dialog>
              </TabsContent>

              <TabsContent value="formulations" className="mt-4">
                <div className="flex justify-end mb-3">
                  {forms.length > 0 && (
                    <Button variant="outline" size="sm" onClick={deleteAllFormulations} data-testid="admin-delete-all-formulations"
                      className="text-red-600 border-red-200 hover:bg-red-50">
                      <Trash2 className="w-4 h-4 mr-1" /> Delete All ({forms.length})
                    </Button>
                  )}
                </div>
                <div className="space-y-2">
                  {forms.map((f) => (
                    <div key={f.id} className="bg-white rounded-xl border border-slate-200 p-4" data-testid={`admin-formulation-${f.id}`}>
                      <div className="flex items-center gap-3 mb-2">
                        <FlaskConical className="w-5 h-5 text-orange-500" />
                        <p className="font-medium text-slate-900">{f.request?.elastomer_type || "Custom"} — {f.request?.application || "General"}</p>
                        <span className="ml-auto text-xs text-slate-500">{f.user_email}</span>
                        <button onClick={() => deleteFormulation(f)} title="Delete formulation" data-testid={`delete-formulation-${f.id}`}
                          className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                      <p className="text-xs text-slate-500">{new Date(f.created_at).toLocaleString()}</p>
                      <details className="mt-2"><summary className="cursor-pointer text-sm text-orange-500">View response</summary>
                        <div className="mt-2 prose prose-sm max-w-none border-t border-slate-100 pt-2">
                          <ReactMarkdown remarkPlugins={[remarkGfm]}>{(f.response || "").slice(0, 1500)}</ReactMarkdown>
                        </div>
                      </details>
                    </div>
                  ))}
                </div>
              </TabsContent>

              <TabsContent value="feedback" className="mt-4">
                {feedback.length === 0 ? (
                  <div className="bg-white rounded-xl border border-slate-200 p-8 text-center text-slate-500">No feedback yet — users haven't rated any answers.</div>
                ) : (
                  <div className="space-y-2">
                    {feedback.map((fb) => (
                      <div key={fb.id} className={`bg-white rounded-xl border p-4 ${fb.rating === "up" ? "border-green-200" : "border-red-200"}`} data-testid={`admin-feedback-${fb.id}`}>
                        <div className="flex items-center gap-2 mb-2">
                          {fb.rating === "up" ? <ThumbsUp className="w-4 h-4 text-green-500" /> : <ThumbsDown className="w-4 h-4 text-red-500" />}
                          <span className="text-sm font-medium text-slate-900">{fb.user_email}</span>
                          <span className="text-xs text-slate-500 ml-auto">{new Date(fb.created_at).toLocaleString()}</span>
                        </div>
                        {fb.user_message && <p className="text-xs text-slate-500 mb-1"><b>Q:</b> {fb.user_message.slice(0, 200)}</p>}
                        <p className="text-sm text-slate-700"><b>A:</b> {fb.assistant_message.slice(0, 400)}</p>
                        {fb.comment && <p className="text-xs text-slate-600 mt-2 italic">"{fb.comment}"</p>}
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>

              <TabsContent value="topics" className="mt-4">
                <div className="bg-white rounded-xl border border-slate-200 p-5">
                  <h3 className="font-heading font-semibold text-slate-900 mb-3">Topic Frequency (all-time, all users)</h3>
                  {topics.length === 0 ? <p className="text-slate-400 text-sm">No data yet</p> : (
                    <div className="space-y-2">
                      {topics.map((t) => {
                        const max = Math.max(...topics.map((x) => x.count));
                        const pct = (t.count / max) * 100;
                        return (
                          <div key={t.topic}>
                            <div className="flex items-center justify-between text-sm">
                              <span className="text-slate-700 capitalize">{t.topic}</span>
                              <span className="text-slate-500 text-xs">{t.count}</span>
                            </div>
                            <div className="h-2 bg-slate-100 rounded-full overflow-hidden"><div className="h-full bg-orange-500 rounded-full" style={{ width: `${pct}%` }} /></div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </TabsContent>

              <TabsContent value="bulk-import" className="mt-4">
                <div className="space-y-4">
                  <div className="bg-white rounded-xl border border-slate-200 p-6">
                    <div className="flex items-center gap-3 mb-2">
                      <Package className="w-5 h-5 text-orange-500" />
                      <h3 className="font-heading font-semibold text-slate-900">Upload ZIP of PDFs / TXTs</h3>
                    </div>
                    <p className="text-sm text-slate-500 mb-4">
                      Drop a <code className="px-1 py-0.5 bg-slate-100 rounded text-xs">.zip</code> file containing PDFs or .txt files. They'll be extracted, chunked, and indexed into the knowledge base. Each file becomes ~1.5KB chunks.
                    </p>

                    <label className="flex items-center gap-2 mb-3 text-sm cursor-pointer">
                      <input type="checkbox" checked={bulkShared} onChange={(e) => setBulkShared(e.target.checked)} data-testid="bulk-shared-checkbox" className="rounded" />
                      <span className="text-slate-700">Mark all chunks as <b>Shared</b> (visible to all users in RAG)</span>
                    </label>

                    <div
                      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                      onDragLeave={() => setDragOver(false)}
                      onDrop={handleDrop}
                      onClick={() => document.getElementById("bulk-zip-input")?.click()}
                      data-testid="bulk-dropzone"
                      className={`border-2 border-dashed rounded-xl p-10 text-center cursor-pointer transition-colors ${
                        dragOver ? "border-orange-500 bg-orange-50" : bulkUploading || bulkActiveJobId ? "border-blue-300 bg-blue-50/50 pointer-events-none" : "border-slate-300 hover:border-orange-400 hover:bg-slate-50"
                      }`}
                    >
                      <input
                        id="bulk-zip-input" type="file" accept=".zip" className="hidden"
                        onChange={(e) => handleBulkUpload(e.target.files?.[0])}
                        data-testid="bulk-file-input"
                      />
                      {bulkUploading ? (
                        <>
                          <Loader2 className="w-12 h-12 text-blue-500 animate-spin mx-auto mb-3" />
                          <p className="text-slate-700 font-medium">Uploading {bulkUploadName}…</p>
                          <div className="max-w-md mx-auto mt-3">
                            <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                              <div className="h-full bg-blue-500 rounded-full transition-all" style={{ width: `${bulkUploadPct}%` }} />
                            </div>
                            <p className="text-xs text-slate-500 mt-2">{bulkUploadPct}%</p>
                          </div>
                        </>
                      ) : bulkActiveJobId ? (
                        <>
                          <Loader2 className="w-12 h-12 text-orange-500 animate-spin mx-auto mb-3" />
                          <p className="text-slate-700 font-medium">Processing ZIP in background…</p>
                          <p className="text-xs text-slate-500 mt-2">Watch the job below for live progress. You can leave this page.</p>
                        </>
                      ) : (
                        <>
                          <Archive className="w-12 h-12 text-slate-400 mx-auto mb-3" />
                          <p className="text-slate-700 font-medium">Drag a ZIP here or click to browse</p>
                          <p className="text-xs text-slate-500 mt-2">Accepts .zip containing PDFs and .txt files. <b>Max 95 MB per upload.</b> Split larger libraries into multiple ZIPs.</p>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Active jobs progress */}
                  <div className="bg-white rounded-xl border border-slate-200 p-6">
                    <h3 className="font-heading font-semibold text-slate-900 mb-3">Import Jobs ({bulkJobs.length})</h3>
                    {bulkJobs.length === 0 ? (
                      <p className="text-sm text-slate-400 text-center py-6">No imports yet. Upload a ZIP above to get started.</p>
                    ) : (
                      <div className="space-y-3">
                        {bulkJobs.map((j) => {
                          const pct = j.total_files > 0 ? Math.round((j.processed_files / j.total_files) * 100) : 0;
                          const isActive = j.status === "queued" || j.status === "processing";
                          // ETA calculation
                          let etaText = null;
                          if (isActive && j.total_files > 0 && j.processed_files > 0) {
                            const startMs = new Date(j.created_at).getTime();
                            const elapsedMs = Date.now() - startMs;
                            const ratePerFile = elapsedMs / j.processed_files;
                            const remainingFiles = j.total_files - j.processed_files;
                            const remainingMs = remainingFiles * ratePerFile;
                            const elapsedMin = Math.round(elapsedMs / 60000);
                            const remMin = Math.round(remainingMs / 60000);
                            const fmt = (m) => m >= 60 ? `${(m / 60).toFixed(1)}h` : `${m}m`;
                            const chunkRate = elapsedMs > 0 ? Math.round(j.chunks_inserted / (elapsedMs / 60000)) : 0;
                            etaText = `Elapsed ${fmt(elapsedMin)} • ETA ~${fmt(remMin)} • ${chunkRate} chunks/min`;
                          } else if (isActive) {
                            etaText = "Starting…";
                          }
                          return (
                            <div key={j.id} className="border border-slate-200 rounded-lg p-4" data-testid={`bulk-job-${j.id}`}>
                              <div className="flex items-center gap-3 mb-2">
                                <Archive className={`w-5 h-5 ${isActive ? "text-orange-500" : j.status === "completed" ? "text-green-500" : "text-red-500"}`} />
                                <div className="flex-1 min-w-0">
                                  <p className="font-medium text-slate-900 truncate">{j.filename}</p>
                                  <p className="text-xs text-slate-500">
                                    {Math.round((j.size_bytes || 0) / 1024 / 1024)} MB • {new Date(j.created_at).toLocaleString()}
                                  </p>
                                </div>
                                <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                                  j.status === "completed" ? "bg-green-100 text-green-700" :
                                  j.status === "failed" ? "bg-red-100 text-red-700" :
                                  isActive ? "bg-orange-100 text-orange-700" : "bg-slate-100 text-slate-600"
                                }`}>
                                  {j.status}
                                </span>
                                {isActive ? (
                                  <button onClick={() => cancelBulkJob(j.id)} data-testid={`cancel-bulk-job-${j.id}`}
                                    className="p-1.5 text-amber-600 hover:bg-amber-50 rounded-lg" title="Cancel in-progress import" disabled={j.cancel_requested}>
                                    <Ban className="w-4 h-4" />
                                  </button>
                                ) : (
                                  <>
                                    <button onClick={() => viewBulkChunks(j.id)} data-testid={`view-bulk-chunks-${j.id}`}
                                      className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg" title="View chunks">
                                      <Eye className="w-4 h-4" />
                                    </button>
                                    <button onClick={() => deleteBulkJob(j.id)} data-testid={`delete-bulk-job-${j.id}`}
                                      className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg" title="Delete job + all its chunks">
                                      <Trash2 className="w-4 h-4" />
                                    </button>
                                  </>
                                )}
                              </div>
                              {isActive && (
                                <>
                                  <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                                    <div className="h-full bg-orange-500 rounded-full transition-all" style={{ width: `${pct}%` }} />
                                  </div>
                                  <div className="flex items-center justify-between text-xs text-slate-500 mt-1">
                                    <span>{j.processed_files} / {j.total_files} files</span>
                                    <span>{j.chunks_inserted} chunks inserted</span>
                                    <span>{pct}%</span>
                                  </div>
                                  {etaText && (
                                    <p className="text-xs text-orange-600 mt-2 font-medium" data-testid={`bulk-eta-${j.id}`}>
                                      ⏱ {etaText}
                                      {j.cancel_requested && <span className="ml-2 text-amber-600">• Cancelling…</span>}
                                    </p>
                                  )}
                                </>
                              )}
                              {j.status === "completed" && (
                                <p className="text-sm text-slate-600 mt-1">
                                  ✅ <b>{j.chunks_inserted}</b> chunks inserted from <b>{j.processed_files}</b> files
                                  {j.failed_files?.length > 0 && <span className="text-amber-600"> • {j.failed_files.length} files failed</span>}
                                  {j.shared && <span className="ml-2 text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded-full">Shared</span>}
                                </p>
                              )}
                              {j.status === "cancelled" && (
                                <p className="text-sm text-amber-600 mt-1">
                                  🛑 Cancelled — kept <b>{j.chunks_inserted}</b> chunks from <b>{j.processed_files}</b> files
                                </p>
                              )}
                              {j.status === "failed" && j.error && (
                                <p className="text-sm text-red-600 mt-1">❌ {j.error}</p>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </TabsContent>
            </Tabs>

            {/* Chunks viewer dialog for bulk imports */}
            <Dialog open={!!chunksDialog} onOpenChange={(o) => !o && setChunksDialog(null)}>
              <DialogContent className="sm:max-w-3xl max-h-[85vh]">
                <DialogHeader><DialogTitle>Chunks in this import ({chunksDialog?.total ?? 0})</DialogTitle></DialogHeader>
                <ScrollArea className="h-[60vh] pr-3">
                  {chunksDialog?.chunks?.length === 0 ? (
                    <p className="text-slate-400 text-sm py-6 text-center">No chunks found.</p>
                  ) : chunksDialog?.chunks?.map((c) => (
                    <div key={c.id} className="mb-3 p-3 rounded-lg bg-slate-50 border border-slate-200">
                      <div className="flex items-center justify-between mb-1">
                        <p className="text-sm font-semibold text-slate-900 truncate">{c.title}</p>
                        {c.is_shared && <span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded-full">Shared</span>}
                      </div>
                      <p className="text-xs text-slate-500 mb-2 truncate">{c.source_file}</p>
                      <p className="text-xs text-slate-700 leading-relaxed">{c.content?.slice(0, 600)}{c.content?.length > 600 && "…"}</p>
                    </div>
                  ))}
                </ScrollArea>
                {chunksDialog && chunksDialog.total > chunksDialog.limit && (
                  <div className="flex items-center justify-between text-sm pt-2 border-t border-slate-200">
                    <Button size="sm" variant="outline" disabled={chunksDialog.page === 0} onClick={() => viewBulkChunks(chunksDialog.jobId, chunksDialog.page - 1)}>← Prev</Button>
                    <span className="text-slate-500">Page {chunksDialog.page + 1} of {Math.ceil(chunksDialog.total / chunksDialog.limit)}</span>
                    <Button size="sm" variant="outline" disabled={(chunksDialog.page + 1) * chunksDialog.limit >= chunksDialog.total} onClick={() => viewBulkChunks(chunksDialog.jobId, chunksDialog.page + 1)}>Next →</Button>
                  </div>
                )}
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </div>
    </div>
  );
}
